'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, KeyRound, Loader2, Lock, Mail, ShieldAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import authApi from '@/lib/api/auth.api';

const requestSchema = z.object({
  identifier: z.string().min(3, 'Please enter your registered email address or mobile number'),
});

const resetSchema = z
  .object({
    token: z.string().min(4, 'Please enter the reset token or 6-digit SMS OTP'),
    newPassword: z.string().min(8, 'Password must be at least 8 characters long'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type RequestSchemaType = z.infer<typeof requestSchema>;
type ResetSchemaType = z.infer<typeof resetSchema>;

export default function ForgotPasswordPage() {
  const router = useRouter();
  const [step, setStep] = useState<'request' | 'reset' | 'success'>('request');
  const [loading, setLoading] = useState(false);
  const [devInfo, setDevInfo] = useState<any>(null);

  const requestForm = useForm<RequestSchemaType>({
    resolver: zodResolver(requestSchema),
    defaultValues: { identifier: '' },
  });

  const resetForm = useForm<ResetSchemaType>({
    resolver: zodResolver(resetSchema),
    defaultValues: { token: '', newPassword: '', confirmPassword: '' },
  });

  const onRequestSubmit = async (values: RequestSchemaType) => {
    setLoading(true);
    try {
      const res = await authApi.forgotPassword(values.identifier);
      toast.success('Reset Request Received', {
        description: res.message || 'If an account exists, instructions have been sent.',
      });
      if (res.data?.devInfo) {
        setDevInfo(res.data.devInfo);
        if (res.data.devInfo.rawTokenOrOtp) {
          resetForm.setValue('token', res.data.devInfo.rawTokenOrOtp);
        }
      }
      setStep('reset');
    } catch (err: any) {
      toast.error('Request Error', {
        description: err.response?.data?.message || 'Failed to submit reset request.',
      });
    } finally {
      setLoading(false);
    }
  };

  const onResetSubmit = async (values: ResetSchemaType) => {
    setLoading(true);
    try {
      const res = await authApi.resetPassword(values.token, values.newPassword);
      if (res.success || (res as any)?.message) {
        toast.success('Password Reset Successful', {
          description: 'All active sessions have been invalidated. Please sign in with your new password.',
        });
        setStep('success');
      }
    } catch (err: any) {
      toast.error('Reset Failed', {
        description: err.response?.data?.message || 'Invalid token or password policy error.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-slate-800/60 bg-slate-900/60 backdrop-blur-xl shadow-2xl">
      {step === 'request' && (
        <>
          <CardHeader className="space-y-1">
            <CardTitle className="text-xl font-bold tracking-tight text-white">
              Forgot Password
            </CardTitle>
            <CardDescription className="text-slate-400">
              Enter your registered email address or mobile number to receive reset instructions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...requestForm}>
              <form onSubmit={requestForm.handleSubmit(onRequestSubmit)} className="space-y-4">
                <FormField
                  control={requestForm.control}
                  name="identifier"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-slate-300">Email or Mobile Number</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                          <Input
                            placeholder="admin@bhulekhbuddy.in or +919876543210"
                            className="pl-10 border-slate-800 bg-slate-950/50 text-white placeholder-slate-600 focus-visible:ring-blue-500/50"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage className="text-rose-400" />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white h-10 font-medium"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending Instructions...
                    </>
                  ) : (
                    'Send Reset Token / OTP'
                  )}
                </Button>

                <div className="pt-2 text-center">
                  <Link
                    href="/login"
                    className="inline-flex items-center text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" /> Back to Sign In
                  </Link>
                </div>
              </form>
            </Form>
          </CardContent>
        </>
      )}

      {step === 'reset' && (
        <>
          <CardHeader className="space-y-1">
            <CardTitle className="text-xl font-bold tracking-tight text-white">
              Reset Your Password
            </CardTitle>
            <CardDescription className="text-slate-400">
              Enter the reset token/OTP sent to your email or SMS, and set your new password
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {devInfo && (
              <div className="p-3 border border-amber-500/30 bg-amber-500/10 rounded-lg text-amber-300 text-xs flex items-start gap-2">
                <ShieldAlert className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <span className="font-semibold block">Dev Test Token ({devInfo.channel}):</span>
                  <code className="bg-slate-950 px-2 py-0.5 rounded font-mono text-white select-all">{devInfo.rawTokenOrOtp}</code>
                </div>
              </div>
            )}

            <Form {...resetForm}>
              <form onSubmit={resetForm.handleSubmit(onResetSubmit)} className="space-y-4">
                <FormField
                  control={resetForm.control}
                  name="token"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-slate-300">Reset Token / OTP</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <KeyRound className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                          <Input
                            placeholder="Enter 6-digit OTP or signed token"
                            className="pl-10 border-slate-800 bg-slate-950/50 text-white font-mono placeholder-slate-600 focus-visible:ring-blue-500/50"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage className="text-rose-400" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={resetForm.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-slate-300">New Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                          <Input
                            type="password"
                            placeholder="••••••••"
                            className="pl-10 border-slate-800 bg-slate-950/50 text-white placeholder-slate-600 focus-visible:ring-blue-500/50"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage className="text-rose-400" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={resetForm.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-slate-300">Confirm New Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                          <Input
                            type="password"
                            placeholder="••••••••"
                            className="pl-10 border-slate-800 bg-slate-950/50 text-white placeholder-slate-600 focus-visible:ring-blue-500/50"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage className="text-rose-400" />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white h-10 font-medium"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating Password...
                    </>
                  ) : (
                    'Reset Password & Invalidate All Sessions'
                  )}
                </Button>

                <div className="pt-2 text-center">
                  <button
                    type="button"
                    onClick={() => setStep('request')}
                    className="text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    Request a new token / OTP
                  </button>
                </div>
              </form>
            </Form>
          </CardContent>
        </>
      )}

      {step === 'success' && (
        <>
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto bg-emerald-500/10 p-3 rounded-full w-fit mb-2">
              <CheckCircle2 className="h-10 w-10 text-emerald-400" />
            </div>
            <CardTitle className="text-xl font-bold tracking-tight text-white">
              Password Reset Complete!
            </CardTitle>
            <CardDescription className="text-slate-400">
              Your password has been successfully updated. Per security requirements, all prior active sessions across all devices have been logged out.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            <Button
              onClick={() => router.push('/login')}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white h-10 font-medium"
            >
              Sign In with New Password
            </Button>
          </CardContent>
        </>
      )}
    </Card>
  );
}
